<template>
  <section class="files-section grid-block">
    <header>
      <span>Файлы</span>

      <!-- Одна сущность: кнопки загрузки и «Скачать всё» в заголовке. -->
      <div
        v-if="isSingleMode"
        class="header-actions"
      >
        <VFilesUploadButton
          v-if="canUpload"
          :entity="entities[0]"
          theme="link"
        >
          Загрузить файл
        </VFilesUploadButton>

        <VButton
          :disabled="isEmptyRow(data[0])"
          theme="link"
          @click="downloadGroup(data)"
        >
          <template #icon>
            <VIcon
              material="download"
              theme="outlined"
            />
          </template>
          Скачать всё
        </VButton>
      </div>

      <!-- Несколько сущностей: одна кнопка загрузки через диалог выбора. -->
      <div v-else>
        <VButton
          v-if="canUpload"
          theme="link"
          @click="openSelectUploadDialog"
        >
          <template #icon>
            <VIcon
              theme="outlined"
              material="upload"
            />
          </template>
          Загрузить файлы
        </VButton>
      </div>
    </header>

    <div class="content">
      <VDataGridV2
        :data="data"
        class="files-grid"
        :class="{ 'hide-group': isSingleMode }"
        clickable
        group-children-property="children"
        layout="fill"
        :context-menu="FilesService.contextMenu"
        :group-context-menu="FilesService.groupContextMenu"
        @row:click="row => preview(row)"
      >
        <template #empty>
          <div v-if="isFilesGridLoading">
            Файлы загружаются, подождите, пожалуйста 🙏...
          </div>
          <div v-else>
            Нет файлов
          </div>
        </template>

        <!-- Группировка для множественных сущностей -->
        <VDataGridGroupV2>
          <template #default="{ parent, children }">
            {{ parent.data.name }}

            <!-- Справочная информация в шапке группы. Пусто по умолчанию. -->
            <slot
              name="group-hint"
              :entity="parent.data"
            />

            <div v-if="$core.utils.isEmptyArray(children) && isFilesGridLoading">
              Файлы загружаются, подождите, пожалуйста 🙏...
            </div>
          </template>
        </VDataGridGroupV2>

        <!-- Колонка иконки расширения -->
        <VDataGridColumnV2
          id="extension"
          content-align="center"
          width="75px"
        >
          <template #default="{ row }">
            <div
              :class="{ 'grey': row.isAlreadyDownloaded }"
            >
              <img
                :src="row.fileIcon"
                :alt="row.extension + ' file'"
              />
              <div class="description">
                {{ row.formattedSize }}
              </div>
            </div>
          </template>
        </VDataGridColumnV2>

        <!-- Колонка названия файла -->
        <VDataGridColumnV2
          id="name"
          width="365px"
          content-class="ellipsis"
        >
          <template #label>
            <div>
              Наименование файла
            </div>
            <div class="description">Дата загрузки</div>
          </template>

          <template #default="{ row }">
            <div :title="row.name" class="ellipsis">
              {{ row.name }}
              <div class="description">
                {{ row.createdAt.format('DD|MMMM|HH|mm') }}
              </div>
            </div>
          </template>
        </VDataGridColumnV2>

        <!-- Колонка действий -->
        <VDataGridColumnV2
          id="extra-actions"
          width="45px"
          content-align="right"
        >
          <template #default="{ row }">
            <div
              class="action-container"
              @click.stop
            >
              <VButton
                :title="row.isAlreadyDownloaded ? 'Файл уже был скачан' : 'Скачать файл'"
                theme="default"
                @click="FilesService.downloadFile(row)"
              >
                <template #icon>
                  <VIcon
                    :class="{ 'downloaded': row.isAlreadyDownloaded }"
                    material="file_download"
                  />
                </template>
              </VButton>
            </div>
          </template>
        </VDataGridColumnV2>
      </VDataGridV2>
    </div>

    <!-- Overlay для drag&drop -->
    <div
      v-if="
          FilesService.isDragging &&
          !FilesService.isDragDropLocked &&
          !isSelectUploadDialogOpen
        "
      class="drop-overlay-container"
    >

      <!-- одна сущность: потребность, товар, партнер -->

      <template v-if="isSingleMode">
        <VFilesDropZone
          v-for="section in overlayData"
          :key="section.key"
          :max-one-file="maxOneFile"
          :entity="entitiesMap.get(section.key)"
          :class="{ 'fullscreen': isSingleMode }"
        />
      </template>

      <!-- несколько сущностей: заказ/позиция -->

      <template v-else>
        <VFilesDropZone
          v-for="section in overlayData"
          :key="section.key"
          :max-one-file="maxOneFile"
          :entity="entitiesMap.get(section.key)"
          :style="{
              position: 'absolute',
              left:   section.left + 'px',
              top:    section.top + 'px',
              width:  section.width + 'px',
              height: section.height + 'px'
            }"
        />
      </template>
    </div>
  </section>
</template>

<style lang="scss" src="./FilesList.scss"/>

<script lang="ts">

import FilesService  from '@/core/services/Files/FilesService';
import FileDescriptor from '@/core/services/Files/FileDescriptor';
import EntityPointer from "@/domains/EntityPointer/EntityPointer";

/**
 * Строка грида: сущность (EntityPointer) с прикреплёнными к ней файлами.
 * id — ключ вида "typeName:id", используется датагридом и для матчинга overlay.
 */
type FilesGridRow = {
  id:       string;
  data:     EntityPointer;
  children: FileDescriptor[];
};

/** Прямоугольник дроп-зоны для overlay (single — на весь экран, multi — по группам грида). */
type OverlaySection = {
  key:    string;
  left:   number;
  top:    number;
  right?: number;
  bottom?: number;
  width:  number;
  height: number;
};

export default defineComponent({
  name: 'FilesList',

  props: {
    /** Сущности, к которым привязаны файлы. Одна → single mode, несколько → группировка по сущностям. */
    entities: {
      type:     Array as PropType<EntityPointer[]>,
      required: true,
    },
    /** Режим одного файла — скрывает кнопки загрузки если файл уже есть. */
    maxOneFile: {
      type:    Boolean,
      default: false,
    },
  },

  emits: ['file:isDragging'],

  setup() {
    return {
      FilesService,
    };
  },

  data() {
    return {
      isFilesGridLoading:       false,
      isSelectUploadDialogOpen: false,
      data:                     [] as FilesGridRow[],
    };
  },

  created() {
    this.prepareData([]);
    this.$core.eventBus.FilesUploaded.on(this.handleFilesEvent);
    this.$core.eventBus.FilesDeleted.on(this.handleFilesEvent);
    /** если файл не дотащили до дроп зоны, то вызываем диалог с выбором сущности */
    this.$core.eventBus.FilesDroppedOutsideZone.on(this.openSelectUploadDialog);
  },

  mounted() {
    this.fetchData();
  },

  beforeUnmount() {
    this.$core.eventBus.FilesUploaded.off(this.handleFilesEvent);
    this.$core.eventBus.FilesDeleted.off(this.handleFilesEvent);
    this.$core.eventBus.FilesDroppedOutsideZone.off(this.openSelectUploadDialog);
  },

  computed: {
    canUpload(): boolean {
      if (!this.maxOneFile) {
        return true;
      }
      else {
        return this.isEmptyRow(this.data[0]);
      }
    },

    isSingleMode(): boolean {
      return this.entities.length === 1;
    },

    overlayData(): OverlaySection[] {
      let raw: OverlaySection[] = [];

      if (this.isSingleMode) {
        raw = [{
          key:    `${this.entities[0].typeName}:${this.entities[0].id}`,
          left:   0,
          top:    0,
          width:  window.innerWidth,
          height: window.innerHeight,
        }];
      }
      else if (FilesService.isDragging) {
        raw = this.getOverlayData();
      }

      return raw.filter(section => this.entitiesMap.has(section.key));
    },

    entitiesMap(): Map<string, EntityPointer> {
      return new Map(this.entities.map(entity => [entity.typeName + ':' + entity.id, entity]));
    },
  },

  methods: {
    async downloadGroup(parent: FilesGridRow | FilesGridRow[]) {
      // строка
      const entity = Array.isArray(parent) ? parent[0] : parent;

      // у строки есть поле с детьми, и с информацией о сущности к которой они прикрепляются
      const { data, children } = entity;

      /** для одной сущности ?? для конкретной из нескольких */

      if (await this.$core.dialogs.confirm(
        `Вы уверены, что хотите скачать все файлы?`,
        `${data.name}`
      )) {
        children.forEach(file => {
          FilesService.downloadFile(file);
        });
      }
    },

    isEmptyRow(row?: FilesGridRow): boolean {
      return this.$core.utils.isEmptyArray(row?.children);
    },

    openSelectUploadDialog({ files }: { files: File[] }) {
      if (FilesService.isDragDropLocked) {
        return;
      }

      // dataTransfer.files - это readonly свойство.
      // из-за этого мы не можем срезать файлы, мы можем только кинуть ошибку
      if (this.maxOneFile && files.length > 1) {
        this.$core.error('Вы можете прикрепить только один файл');
        return;
      }

      this.isSelectUploadDialogOpen = true;
      this.$core.dialogs.FilesSelectUploadDialog.show(
        {
          entities:     this.entities,
          droppedFiles: files, // файлы, которые перетащили вне дроп зоны
        },
        {
          'close': () => {
            this.isSelectUploadDialogOpen = false;
            FilesService.resetDragState();
          }
        }
      );
    },

    async fetchData() {
      this.isFilesGridLoading = true;

      // получаем список файлов от сервиса
      const data = await FilesService.getList(this.entities);
      // готовим данные с учетом особенностей группировки
      this.data = this.prepareData(data);
      this.isFilesGridLoading = false;

      // После обновления data пересчитываем блокировку drag&drop:
      // в режиме maxOneFile дроп-зона должна блокироваться, если файл уже прикреплён.
      this.applyLockState();
    },

    refresh() {
      return this.fetchData();
    },

    /**
     * Блокировка/разблокировка drag&drop в зависимости от canUpload.
     * Вызывается после каждого обновления data (fetchData) — заменяет watch
     * на canUpload, который был раньше.
     */
    applyLockState() {
      if (this.canUpload === true) {
        FilesService.unlockDragDrop();
      }
      else {
        FilesService.lockDragDrop();
      }
    },

    async preview(file: FileDescriptor) {
      if (!file.isPreviewable) {
        return;
      }
      /** После превью нужно показать, что он уже скачен */
      await FilesService.previewFile(file);
      await this.fetchData();
    },

    /**
     * Преобразует ответ сервера в формат для VDataGridV2 с группировкой.
     *
     * Сервер отвечает по-разному в зависимости от количества сущностей:
     * - Одна сущность (потребность, товар, партнёр): плоский массив файлов
     * - Несколько сущностей (заказ + позиции): объект { "typeName:id": файлы[] }
     *
     * Метод нормализует оба формата в единую структуру для датагрида:
     * [{ id: "typeName:id", data: entity, children: файлы[] }, ...]
     */
    prepareData(data: FileDescriptor[] | Record<string, FileDescriptor[]> | null): FilesGridRow[] {
      if (!Array.isArray(this.entities) || this.entities.length === 0) {
        return [];
      }

      let normalized: Record<string, FileDescriptor[]>;

      // Если пришёл плоский массив (single mode) — оборачиваем в объект с ключом,
      // чтобы дальше обрабатывать единообразно
      if (Array.isArray(data)) {
        const singleEntity = this.entities[0];
        if (!singleEntity) {
          return [];
        }

        normalized = {
          [`${singleEntity.typeName}:${singleEntity.id}`]: data,
        };
      }
      else {
        normalized = data ?? {};
      }

      // Для каждой сущности из props достаём её файлы из нормализованного объекта
      return this.entities.map(entity => {
        const dataKey         = `${entity.typeName}:${entity.id}`;
        const entityFilesData = normalized[dataKey] || [];

        return {
          id:       dataKey,
          data:     entity,
          children: entityFilesData,
        };
      });
    },

    // определяем является ли событие релевантным именно для этого списка файлов и обновляем его если это так
    handleFilesEvent(eventData: { entity?: EntityPointer }) {
      if (eventData.entity) {
        const isRelevant = this.entities.some(entity =>
          entity.typeName === eventData.entity!.typeName &&
          entity.id === eventData.entity!.id
        );
        if (isRelevant) {
          this.refresh();
        }
      }
      else {
        this.refresh();
      }
    },

    /**
     * Вычисляет координаты прямоугольника для группы строк
     * @param headerRow - элемент строки-заголовка с классом group-header
     */
    getGroupBounds(headerRow: HTMLElement): OverlaySection {
      if (!headerRow || !headerRow.classList.contains('group-header')) {
        throw new Error('Передан некорректный элемент группы-заголовка');
      }
      // Получаем прямоугольник заголовка группы
      const headerRect = headerRow.getBoundingClientRect();
      // Находим последнюю строку текущей группы
      let lastRow: HTMLElement = headerRow;
      let nextRow = headerRow.nextElementSibling;
      while (nextRow && !nextRow.classList.contains('group-header')) {
        lastRow = nextRow as HTMLElement;
        nextRow = nextRow.nextElementSibling;
      }
      const lastRowRect = lastRow.getBoundingClientRect();
      // Вычисляем координаты общего прямоугольника
      const sectionData: OverlaySection = {
        key:    headerRow.dataset.rowIdentity || '',
        left:   Math.min(headerRect.left, lastRowRect.left),
        top:    headerRect.top,
        right:  Math.max(headerRect.right, lastRowRect.right),
        bottom: lastRowRect.bottom,
        width:  0,
        height: 0,
      };
      sectionData.width  = sectionData.right! - sectionData.left;
      sectionData.height = sectionData.bottom! - sectionData.top;
      return sectionData;
    },

    /**
     * Функция для получения границ всех групп на странице
     */
    getOverlayData(): OverlaySection[] {
      const groupHeaders = document.querySelectorAll<HTMLElement>('.group-header');

      // Если есть группы — собираем bounds по группам
      if (groupHeaders.length > 0) {
        return Array.from(groupHeaders)
          .map(header => this.getGroupBounds(header));
      }

      // Если групп нет — возвращаем bounds всего грида
      const gridEl = document.querySelector('.files-grid');
      if (!gridEl) {
        return [];
      }

      const rect = gridEl.getBoundingClientRect();
      return [{
        key:    this.entities[0].typeName + ':' + this.entities[0].id,
        left:   rect.left,
        top:    rect.top,
        right:  rect.right,
        bottom: rect.bottom,
        width:  rect.width,
        height: rect.height,
      }];
    },
  },
});
</script>
